import {type EventDispatcher, Raycaster, Vector2,} from 'three';
import {ViewOrientationElementMesh} from '../view-orientation/ViewOrientationElementMesh';
import {ViewOrientationMesh} from '../view-orientation/ViewOrientationMesh';
import type {ViewportGizmoEventMap} from './viewport-gizmo-events';
import {ViewportGizmoEventType} from './ViewportGizmoEventType';
import {ViewportGizmoHoverTargetKind} from './ViewportGizmoHoverTargetKind';
import type {ViewportGizmoRenderer} from './ViewportGizmoRenderer';
import {ViewportGizmoOrbitDragEndReason} from './ViewportGizmoOrbitDragEndReason';
import {isViewportGizmoOuterControlMesh, type ViewportGizmoOuterControlMesh,} from './ViewportGizmoOuterControlMesh';


/** 视口方向导航器交互依赖的最小宿主契约。 */
export interface ViewportGizmoInteractionHost {
  /** 导航器渲染和拾取对象。 */
  readonly gizmo: ViewportGizmoRenderer;
  /** 向公共导航器消费方发布用户交互意图。 */
  readonly eventDispatcher: EventDispatcher<ViewportGizmoEventMap>;
}

/**
 * 使用原生 DOM 事件管理导航器悬停、点击和拖动交互。
 *
 * 该控制器不依赖 RxJS；所有监听器都在 `dispose()` 中按创建位置对称移除。
 */
export class ViewportGizmoInteractionController {
  /** 区分点击与拖动的最小屏幕距离。 */
  private static readonly DRAG_THRESHOLD = 3;

  /** 导航器交互宿主。 */
  private readonly host: ViewportGizmoInteractionHost;

  /** 同时拾取默认图层外部按钮和图层 31 中央元素的射线。 */
  private readonly raycaster = new Raycaster();

  /** 复用的标准化设备坐标。 */
  private readonly pointer = new Vector2();

  /** 复用的当前指针坐标，避免拖动热路径频繁创建对象。 */
  private readonly currentPointerPosition = new Vector2();

  /** 当前是否已挂载原生事件监听。 */
  private started = false;

  /** 当前按下指针标识。 */
  private activePointerId: number | null = null;

  /** 指针按下坐标。 */
  private readonly pointerDownPosition = new Vector2();

  /** 上一次拖动坐标。 */
  private readonly previousPointerPosition = new Vector2();

  /** 当前是否已经越过拖动阈值。 */
  private dragging = false;

  /** 当前悬停中央元素。 */
  private hoveredElement: ViewOrientationElementMesh | null = null;

  /** 当前悬停外部按钮。 */
  private hoveredControl: ViewportGizmoOuterControlMesh | null = null;

  /**
   * 创建导航器交互控制器。
   *
   * @param host 渲染器和公开事件派发端口。
   */
  public constructor(host: ViewportGizmoInteractionHost) {
    this.host = host;
    this.raycaster.layers.set(0);
    this.raycaster.layers.enable(ViewOrientationMesh.MESH_LAYER);
  }

  /** 启动全部原生指针监听；重复调用保持幂等。 */
  public start(): void {
    if (this.started) {
      return;
    }

    const element = this.host.gizmo.domElement;
    const eventDocument = element.ownerDocument;

    // 根元素负责点击与悬停，文档级监听保证指针离开导航器后仍能结束拖动。
    element.addEventListener('pointerdown', this.handlePointerDown);
    element.addEventListener('pointermove', this.handleHover);
    element.addEventListener('pointerleave', this.handlePointerLeave);
    eventDocument.addEventListener('pointermove', this.handleDrag);
    eventDocument.addEventListener('pointerup', this.handlePointerUp);
    eventDocument.addEventListener('pointercancel', this.handlePointerCancel);
    this.started = true;
  }

  /** 解除全部事件监听并恢复悬停和拖动状态。 */
  public dispose(): void {
    if (this.started) {
      const element = this.host.gizmo.domElement;
      const eventDocument = element.ownerDocument;
      element.removeEventListener('pointerdown', this.handlePointerDown);
      element.removeEventListener('pointermove', this.handleHover);
      element.removeEventListener('pointerleave', this.handlePointerLeave);
      eventDocument.removeEventListener('pointermove', this.handleDrag);
      eventDocument.removeEventListener('pointerup', this.handlePointerUp);
      eventDocument.removeEventListener('pointercancel', this.handlePointerCancel);
      this.started = false;
    }

    if (this.dragging && this.activePointerId !== null) {
      this.dispatchOrbitDragEnded(
        this.activePointerId,
        ViewportGizmoOrbitDragEndReason.Disposed,
      );
    }
    this.activePointerId = null;
    this.dragging = false;
    this.clearHover();
  }

  /** 记录左键按下状态，实际动作延迟到移动或抬起。 */
  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || this.activePointerId !== null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.activePointerId = event.pointerId;
    this.pointerDownPosition.set(event.clientX, event.clientY);
    this.previousPointerPosition.copy(this.pointerDownPosition);
    this.dragging = false;
  };

  /** 根据文档级指针移动执行增量环绕。 */
  private readonly handleDrag = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    this.currentPointerPosition.set(event.clientX, event.clientY);
    if (
      !this.dragging
      && this.currentPointerPosition.distanceTo(this.pointerDownPosition)
        >= ViewportGizmoInteractionController.DRAG_THRESHOLD
    ) {
      this.dragging = true;
      this.clearHover();
      this.host.eventDispatcher.dispatchEvent({
        type: ViewportGizmoEventType.OrbitDragStarted,
        pointerId: event.pointerId,
      });
    }
    if (!this.dragging) {
      return;
    }

    event.preventDefault();
    const bounds = this.host.gizmo.domElement.getBoundingClientRect();
    const normalizationSize = Math.max(bounds.width, 1);
    const horizontalAngle = (
      (this.currentPointerPosition.x - this.previousPointerPosition.x)
      / normalizationSize
    ) * Math.PI;
    const verticalAngle = (
      (this.currentPointerPosition.y - this.previousPointerPosition.y)
      / normalizationSize
    ) * Math.PI;

    this.host.eventDispatcher.dispatchEvent({
      type: ViewportGizmoEventType.OrbitDragChanged,
      pointerId: event.pointerId,
      horizontalAngle,
      verticalAngle,
    });
    this.previousPointerPosition.copy(this.currentPointerPosition);
  };

  /** 点击时执行拾取，拖动时结束相机控制权。 */
  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    event.preventDefault();
    if (this.dragging) {
      this.dispatchOrbitDragEnded(
        event.pointerId,
        ViewportGizmoOrbitDragEndReason.Completed,
      );
    } else {
      this.handleClick(event);
    }

    this.activePointerId = null;
    this.dragging = false;
  };

  /** 取消活动指针并恢复主导航控制器。 */
  private readonly handlePointerCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    if (this.dragging) {
      this.dispatchOrbitDragEnded(
        event.pointerId,
        ViewportGizmoOrbitDragEndReason.Cancelled,
      );
    }
    this.activePointerId = null;
    this.dragging = false;
    this.clearHover();
  };

  /** 导航器内移动时同步外部按钮和中央元素悬停。 */
  private readonly handleHover = (event: PointerEvent): void => {
    if (this.activePointerId !== null) {
      return;
    }

    const control = this.pickOuterControl(event);
    if (control) {
      this.updateHover(null, control);
      return;
    }
    this.updateHover(this.pickOrientationElement(event), null);
  };

  /** 指针离开导航器时清空悬停状态。 */
  private readonly handlePointerLeave = (): void => {
    this.clearHover();
  };

  /** 外部按钮优先，随后处理中央 26 向元素。 */
  private handleClick(event: PointerEvent): void {
    const control = this.pickOuterControl(event);
    if (control) {
      this.host.eventDispatcher.dispatchEvent({
        type: ViewportGizmoEventType.ControlActionTriggered,
        pointerId: event.pointerId,
        action: control.action,
      });
      return;
    }

    const element = this.pickOrientationElement(event);
    if (element) {
      this.host.eventDispatcher.dispatchEvent({
        type: ViewportGizmoEventType.StandardViewSelected,
        pointerId: event.pointerId,
        viewType: element.viewType,
      });
    }
  }

  /**
   * 射线拾取固定屏幕方向的外部按钮。
   *
   * @param event 当前指针事件。
   * @returns 命中的外部按钮；未命中时返回 `null`。
   */
  private pickOuterControl(
    event: PointerEvent,
  ): ViewportGizmoOuterControlMesh | null {
    if (!this.prepareRaycaster(event)) {
      return null;
    }

    const intersection = this.raycaster.intersectObjects(
      [...this.host.gizmo.outerControls.getControls()],
      false,
    )[0];
    return intersection && isViewportGizmoOuterControlMesh(intersection.object)
      ? intersection.object
      : null;
  }

  /**
   * 射线拾取中央方向立方体面、边或角。
   *
   * @param event 当前指针事件。
   * @returns 命中的方向元素；未命中时返回 `null`。
   */
  private pickOrientationElement(
    event: PointerEvent,
  ): ViewOrientationElementMesh | null {
    if (!this.prepareRaycaster(event)) {
      return null;
    }

    const intersection = this.raycaster.intersectObjects(
      [...this.host.gizmo.orientationMesh.getAllMeshes()],
      false,
    )[0];
    return intersection?.object instanceof ViewOrientationElementMesh
      ? intersection.object
      : null;
  }

  /**
   * 将事件客户区坐标转换为导航器内部 NDC 射线。
   *
   * @param event 当前指针事件。
   * @returns 导航器具有有效布局区域时返回 `true`。
   */
  private prepareRaycaster(event: PointerEvent): boolean {
    const bounds = this.host.gizmo.domElement.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return false;
    }

    this.pointer.set(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.host.gizmo.pickingCamera);
    return true;
  }

  /**
   * 同步两个互斥拾取组的悬停状态。
   *
   * @param element 当前中央标准视图元素。
   * @param control 当前外部控制按钮。
   */
  private updateHover(
    element: ViewOrientationElementMesh | null,
    control: ViewportGizmoOuterControlMesh | null,
  ): void {
    if (this.hoveredElement === element && this.hoveredControl === control) {
      return;
    }

    this.hoveredElement = element;
    this.hoveredControl = control;
    this.host.gizmo.orientationMesh.setHoveredMesh(element);
    this.host.gizmo.outerControls.setHoveredControl(control);
    this.host.gizmo.domElement.style.cursor = element || control ? 'pointer' : '';

    this.host.eventDispatcher.dispatchEvent({
      type: ViewportGizmoEventType.HoverChanged,
      targetKind: this.resolveHoverTargetKind(element, control),
      viewType: element?.viewType ?? null,
      action: control?.action ?? null,
    });
  }

  /** 清空全部悬停状态。 */
  private clearHover(): void {
    this.updateHover(null, null);
  }

  /**
   * 发布拖动结束事件。
   *
   * @param pointerId 当前活动指针标识。
   * @param reason 拖动结束原因。
   */
  private dispatchOrbitDragEnded(
    pointerId: number,
    reason: ViewportGizmoOrbitDragEndReason,
  ): void {
    this.host.eventDispatcher.dispatchEvent({
      type: ViewportGizmoEventType.OrbitDragEnded,
      pointerId,
      reason,
    });
  }

  /**
   * 根据互斥的中央元素和外部按钮解析公开悬停类型。
   *
   * @param element 当前悬停中央元素。
   * @param control 当前悬停外部按钮。
   * @returns 与当前目标对应的有限类型。
   */
  private resolveHoverTargetKind(
    element: ViewOrientationElementMesh | null,
    control: ViewportGizmoOuterControlMesh | null,
  ): ViewportGizmoHoverTargetKind {
    if (element) {
      return ViewportGizmoHoverTargetKind.StandardView;
    }
    if (control) {
      return ViewportGizmoHoverTargetKind.ControlAction;
    }
    return ViewportGizmoHoverTargetKind.None;
  }
}

